import React, { useState } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, KeyRound, ShieldCheck } from "lucide-react";
import BatLogo from "@/components/BatLogo";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import ForgotPasswordModal from "@/components/ForgotPasswordModal";
import { toast } from "sonner";

export default function Login() {
  const { status, login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  if (status === "authed") return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = await login(username.trim(), password);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error);
      toast.error(res.error);
    } else {
      toast.success("Clearance granted. Welcome to Gotham.");
    }
  };

  return (
    <div className="min-h-screen gotham-bg relative flex items-center justify-center px-4 scanlines" data-testid="login-page">
      <div className="absolute inset-0 pointer-events-none">
        <div className="scan-line" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center text-amber-500 bat-pulse">
            <BatLogo size={110} />
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-black uppercase tracking-tight text-slate-100">
            Vrax's <span className="text-amber-500">Workflow</span>
          </h1>
          <p className="mt-2 label-mono">// Wayne-Tech Secure Terminal • Clearance Level 1</p>
        </div>

        <form
          onSubmit={submit}
          className="card-tactical chamfered p-8 relative"
          data-testid="login-form"
        >
          <div className="flex items-center gap-2 mb-6 text-amber-500">
            <ShieldCheck className="w-4 h-4" />
            <span className="label-mono text-amber-500">Authenticate</span>
          </div>

          <div className="mb-4">
            <label className="label-mono block mb-2">Username</label>
            <input
              className="input-tactical w-full px-4 py-3 rounded"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="vrax"
              autoComplete="username"
              required
              data-testid="login-username-input"
            />
          </div>

          <div className="mb-6">
            <label className="label-mono block mb-2">Password</label>
            <div className="relative">
              <input
                className="input-tactical w-full px-4 py-3 rounded pr-11"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={show ? "text" : "password"}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-amber-400 transition"
                data-testid="toggle-password-visibility"
                aria-label="Toggle password visibility"
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {err && (
            <div
              className="mb-4 px-3 py-2 rounded border border-red-500/40 bg-red-500/10 text-red-300 text-sm font-mono-vrax"
              data-testid="login-error"
            >
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="btn-signal w-full py-3 rounded flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="login-submit-btn"
          >
            <KeyRound className="w-4 h-4" />
            {busy ? "Verifying…" : "Enter Gotham"}
          </button>

          <div className="mt-5 flex items-center justify-between text-xs">
            <span className="label-mono text-slate-500">// Shared Vault</span>
            <button
              type="button"
              onClick={() => setForgotOpen(true)}
              className="text-amber-500 hover:text-amber-300 font-mono-vrax uppercase tracking-wider transition"
              data-testid="forgot-password-link"
            >
              Forgot Password?
            </button>
          </div>
        </form>

        <p className="mt-6 text-center label-mono text-slate-600">
          // Vrax's Workflow • v1.0 • Gotham Division
        </p>
      </motion.div>

      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </div>
  );
}
